import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Check, X, Eye, EyeOff } from "lucide-react";
import { useInertiaForm } from "@/hooks/use-inertia-form";

export default function AuthDefinyPassword(props: { token: string }) {
  const { token } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { form, handleSubmit } = useInertiaForm({
    url: `/reset-password/${token}`, // ajuste para a rota correta se necessário
    method: "post",
    initialData: {
      password: "",
      password_confirmation: "",
      token,
    },
  });

  const password = form.data.password;
  const confirmPassword = form.data.password_confirmation;

  // Validações da senha
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const passwordsMatch = password === confirmPassword && password !== "";

  // Cálculo da força da senha
  const requirements = [
    hasMinLength,
    hasUppercase,
    hasLowercase,
    hasNumber,
    hasSpecialChar,
  ];
  const strengthScore = requirements.filter(Boolean).length;
  const strengthPercentage = (strengthScore / 5) * 100;

  const getStrengthText = () => {
    if (strengthScore <= 2) return "Fraca";
    if (strengthScore <= 3) return "Média";
    if (strengthScore <= 4) return "Forte";
    return "Muito Forte";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle>Definir Nova Senha</CardTitle>
          <CardDescription>
            Crie uma senha segura seguindo os requisitos abaixo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo de Senha */}
            <div className="space-y-2">
              <Label htmlFor="password">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={form.data.password}
                  onChange={(e) => form.setData("password", e.target.value)}
                  placeholder="Digite sua nova senha"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </Button>
              </div>
            </div>

            {/* Indicador de Força da Senha */}
            {password && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Força da senha:</span>
                  <span className="font-medium">{getStrengthText()}</span>
                </div>
                <Progress value={strengthPercentage} className="h-2" />
              </div>
            )}

            {/* Requisitos da Senha */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Requisitos da senha:
              </Label>
              <div className="space-y-1">
                <RequirementItem
                  met={hasMinLength}
                  text="Pelo menos 8 caracteres"
                />
                <RequirementItem
                  met={hasUppercase}
                  text="Pelo menos 1 letra maiúscula (A-Z)"
                />
                <RequirementItem
                  met={hasLowercase}
                  text="Pelo menos 1 letra minúscula (a-z)"
                />
                <RequirementItem
                  met={hasNumber}
                  text="Pelo menos 1 número (0-9)"
                />
                <RequirementItem
                  met={hasSpecialChar}
                  text="Pelo menos 1 caractere especial (!@#$%^&*)"
                />
              </div>
            </div>

            {/* Campo de Confirmação */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Senha</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.data.password_confirmation}
                  onChange={(e) =>
                    form.setData("password_confirmation", e.target.value)
                  }
                  placeholder="Confirme sua nova senha"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </Button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <X className="h-3 w-3" />
                  As senhas não coincidem
                </p>
              )}
              {passwordsMatch && confirmPassword && (
                <p className="text-sm text-green-500 flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  As senhas coincidem
                </p>
              )}
            </div>

            {/* Botão de Submit */}
            <Button
              type="submit"
              className="w-full"
              disabled={!requirements.every(Boolean) || !passwordsMatch}
              isLoading={form.processing}
            >
              Definir Senha
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Componente para cada requisito
function RequirementItem({ met, text }: { met: boolean; text: string }) {
  return (
    <div
      className={`flex items-center gap-2 text-sm ${
        met ? "text-green-600" : "text-gray-500"
      }`}
    >
      {met ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <X className="h-3 w-3 text-gray-400" />
      )}
      <span>{text}</span>
    </div>
  );
}
